import { EntityType } from "@mapmycustomers/shared";
import { EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  CombinedCondition,
  FilterModel,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { MapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import SavedFilter, {
  RegularSavedFilter,
} from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import { FilterModelUpdater } from "@app/component/view/types/IViewContext";
import convertMapFilterModelToFilterModel from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import {
  createAndCondition,
  getCombinedConditionIndexForTag,
  getSimpleConditionForField,
} from "@app/util/filters/utils";
import { isSimpleCondition } from "@app/util/viewModel/assert";

export const applySavedFilter = <T extends SavedFilter>(
  savedFilter: T | undefined,
  entityType: EntityTypesSupportingSavedFilters,
  processSavedFilter: ((savedFilter: T) => T | undefined) | undefined,
  implicitFilterFields: IField[] | undefined,
  updateFilterModel: FilterModelUpdater,
  implicitFilterFieldsWithinTag?: CombinedCondition["tag"]
) => {
  if (
    !savedFilter ||
    (savedFilter.type !== entityType &&
      (entityType === EntityType.PIN || entityType === EntityType.ACTIVITY))
  ) {
    return;
  }

  const processedFilter = processSavedFilter ? processSavedFilter(savedFilter) : savedFilter;
  if (!processedFilter || entityType === EntityType.PIN) {
    return;
  }

  const filters: FilterModel =
    entityType === EntityType.ACTIVITY
      ? (processedFilter as RegularSavedFilter).filters
      : convertMapFilterModelToFilterModel(entityType, processedFilter.filters as MapFilterModel);

  // console.log("!!! applying saved filter", entityType, savedFilter, filters);

  updateFilterModel((currentFilters) => {
    const implicitFieldsConditions = new Map(
      (implicitFilterFields ?? []).reduce(
        (result, field) => {
          const condition = getSimpleConditionForField(
            currentFilters,
            field.filterName,
            implicitFilterFieldsWithinTag
          );
          if (condition) {
            result.push([field.filterName, condition]);
          }
          return result;
        },
        [] as [IField["filterName"], SimpleCondition][]
      )
    );

    let resultingFilterModel: FilterModel = filters;
    if (implicitFilterFieldsWithinTag) {
      const combinedConditionIndex = getCombinedConditionIndexForTag(
        filters,
        implicitFilterFieldsWithinTag
      );
      if (combinedConditionIndex >= 0) {
        let combinedCondition = filters.conditions[combinedConditionIndex] as CombinedCondition;
        combinedCondition = {
          ...combinedCondition,
          conditions: combinedCondition.conditions.map((condition) => {
            if (
              isSimpleCondition(condition) &&
              implicitFieldsConditions.has(condition.fieldFilterName)
            ) {
              // we keep conditions for implicit fields from the current filter model
              // because otherwise it may break the view
              // (we have special handling for Deal pages, but not for Planner)
              const implicitFieldCondition = implicitFieldsConditions.get(
                condition.fieldFilterName
              )!;
              implicitFieldsConditions.delete(condition.fieldFilterName);
              return implicitFieldCondition;
            }
            return condition;
          }),
        };
        resultingFilterModel = {
          ...resultingFilterModel,
          conditions: resultingFilterModel.conditions.toSpliced(
            combinedConditionIndex,
            1,
            combinedCondition
          ),
        };
      }
    } else {
      resultingFilterModel = {
        ...resultingFilterModel,
        conditions: filters.conditions.map((condition) => {
          if (
            isSimpleCondition(condition) &&
            implicitFieldsConditions.has(condition.fieldFilterName)
          ) {
            // we keep conditions for implicit fields from the current filter model
            // because otherwise it may break the view
            // (we have special handling for Deal pages, but not for Planner)
            const implicitFieldCondition = implicitFieldsConditions.get(condition.fieldFilterName)!;
            implicitFieldsConditions.delete(condition.fieldFilterName);
            return implicitFieldCondition;
          }
          return condition;
        }),
      };
    }

    if (implicitFilterFieldsWithinTag) {
      const combinedConditionIndex = getCombinedConditionIndexForTag(
        resultingFilterModel,
        implicitFilterFieldsWithinTag
      );

      let combinedCondition =
        combinedConditionIndex >= 0
          ? (resultingFilterModel.conditions[combinedConditionIndex] as CombinedCondition)
          : createAndCondition([], implicitFilterFieldsWithinTag);

      combinedCondition = {
        ...combinedCondition,
        conditions: [...combinedCondition.conditions],
      };

      implicitFieldsConditions.forEach((implicitFieldCondition) => {
        combinedCondition.conditions.push(implicitFieldCondition);
      });

      resultingFilterModel = {
        ...resultingFilterModel,
        conditions:
          combinedConditionIndex >= 0
            ? resultingFilterModel.conditions.toSpliced(
                combinedConditionIndex,
                1,
                combinedCondition
              )
            : [...resultingFilterModel.conditions, combinedCondition],
      };
    } else {
      implicitFieldsConditions.forEach((implicitFieldCondition) => {
        resultingFilterModel.conditions.push(implicitFieldCondition);
      });
    }
    return resultingFilterModel;
  });
};
